import { ChangeDetectionStrategy, Component, input, type InputSignal } from "@angular/core";

/**
 * Which drawing the auth panel shows. One per unauthenticated screen.
 *
 * A union rather than a filename, for the same reason `NavIconName` is one:
 * a typo in a path fails silently as a missing image at runtime, where a typo
 * here fails the build.
 */
export type AuthArtName =
  | "door"        // the staff/customer sign-in door
  | "locked"      // signed in, role missing            (/no-access)
  | "wrong-key"   // signed in, wrong organization      (/forbidden)
  | "expired"     // session timed out
  | "unknown";    // no organization at this address

/**
 * The line drawing on the auth panel.
 *
 * Authored inline rather than as files under `public/assets` so it follows the
 * same rule as the icon set: one typed union, no runtime path to get wrong, and
 * `currentColor` throughout so a single `color` on the host restyles every
 * stroke. The panel is dark, so the host sets white and the fills that must
 * read as solid use the panel's own navy explicitly.
 *
 * The subject is deliberately Davaco's own: a storefront, because the portal
 * tracks retail rollouts. Padlocks-and-keys stock art would say nothing about
 * what this product is.
 *
 * SCOPE. The design system's Imagery rule confines this to the five
 * unauthenticated screens. Nothing behind the sign-in wall may use it — not a
 * card, not an empty state. See `reference.md`, amended 2026-09-26.
 */
@Component({
  selector: "app-auth-art",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./auth-art.component.html",
  styleUrl: "./auth-art.component.scss",
  host: { "[class.art--animated]": "animated()" },
})
export class AuthArtComponent {

  public readonly name: InputSignal<AuthArtName> = input.required<AuthArtName>();

  /**
   * Loop the drawing's small story — today only `wrong-key`, whose key tries
   * the lock, jiggles and backs off as the cross flashes. Off by default: the
   * other screens are static. Stops under `prefers-reduced-motion`.
   */
  public readonly animated: InputSignal<boolean> = input<boolean>(false);
}
